import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { Text, TextInput, Button, Surface } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import { useNavigation } from '@react-navigation/native';
import SlidingMenu from '../components/SlidingMenu';
import axios from 'axios';
import { API_URL } from '../config/api';
import MaxWidthContainer from '../components/MaxWidthContainer';
import HeaderBar from '../components/HeaderBar';
import { brandPrimary, brandButton } from '../theme/paperTheme';

const inputOutline = {
  outlineColor: 'rgba(15, 23, 42, 0.12)',
  activeOutlineColor: brandPrimary,
};

const FeedbackScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setError('Please enter your feedback.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/feedback`, { message: feedback });
      setSuccess(true);
      setFeedback('');
      setTimeout(() => {
        setSuccess(false);
        navigation.goBack();
      }, 1500);
    } catch {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = () => setIsMenuVisible((v) => !v);

  const handleCancel = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Home');
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 0}
    >
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            rightContent={
              <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
            style={styles.headerBar}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Text style={styles.screenLabel}>We'd love to hear from you</Text>
            <Text style={styles.screenTitle}>Share your feedback</Text>
            <Text style={styles.subtitle}>
              Tell us what's working, what could be better, or any issues you've run into.
            </Text>

            <Surface style={styles.formCard} elevation={0}>
              <TextInput
                mode="outlined"
                label="Your feedback"
                value={feedback}
                onChangeText={setFeedback}
                multiline
                numberOfLines={6}
                style={styles.input}
                placeholder="Type your feedback here..."
                error={!!error}
                {...inputOutline}
              />
              {error ? <Text style={styles.error}>{error}</Text> : null}
              {success ? (
                <Text style={styles.success}>Thank you for your feedback!</Text>
              ) : null}

              <Button
                mode="contained"
                onPress={handleSubmit}
                loading={loading}
                disabled={loading || success}
                style={styles.submitButton}
                buttonColor={brandButton}
                textColor="#ffffff"
              >
                Submit feedback
              </Button>

              <Button
                mode="text"
                onPress={handleCancel}
                style={styles.cancelButton}
                textColor={brandPrimary}
              >
                Cancel
              </Button>
            </Surface>
          </ScrollView>
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />
    </KeyboardAvoidingView>
  );
};

const cardShadow = Platform.OS === 'web'
  ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
  : {
      shadowColor: '#0F172A',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.06,
      shadowRadius: 12,
      elevation: 3,
    };

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 10,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 24,
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...cardShadow,
  },
  input: {
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
    minHeight: 140,
  },
  submitButton: {
    marginTop: 8,
    borderRadius: 999,
    paddingVertical: 4,
  },
  cancelButton: {
    marginTop: 4,
  },
  error: {
    color: '#d32f2f',
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 15,
    fontWeight: '500',
  },
  success: {
    color: brandPrimary,
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 15,
    fontWeight: '600',
  },
});

export default FeedbackScreen;
